import { RowActions, StatusTag, titleCase } from '@westhetics/ui'
import { Link } from 'react-router'
import { useSession } from '@/lib/session'
import { routes } from '@/routes'
import type { AnamnesisSummary } from './api'
import {
  STATUS_LEVELS,
  canCancel,
  canResend,
  deliveryLine,
  draftSince,
  formatDateTime,
} from './labels'
import type { ActionTarget } from './use-anamnesis-actions'

/**
 * O que a lista global e a aba da ficha escrevem igual em cada linha: o envio com a entrega, a
 * etiqueta de status por extenso e as ações em texto (RN-ANA-22).
 */

const secondLineClass = 'font-body text-secondary mt-1 text-[12px] leading-[1.5] font-light'

/** "20/09/2026 10:02" e, embaixo, "WhatsApp · lido". */
export function SentCell({ anamnesis }: { anamnesis: AnamnesisSummary }) {
  const delivery = deliveryLine(anamnesis)
  return (
    <div className="min-w-0">
      {formatDateTime(anamnesis.createdAt)}
      {delivery ? <div className={secondLineClass}>{delivery}</div> : null}
    </div>
  )
}

/** A etiqueta por extenso, como a API manda, e desde quando a paciente está respondendo. */
export function StatusCell({ anamnesis }: { anamnesis: AnamnesisSummary }) {
  const since = anamnesis.status === 'in_progress' ? draftSince(anamnesis.draft) : null
  return (
    <div className="min-w-0">
      <StatusTag level={STATUS_LEVELS[anamnesis.status]}>{anamnesis.label}</StatusTag>
      {since ? <div className={secondLineClass}>{since}</div> : null}
    </div>
  )
}

/**
 * As ações da linha. Preenchida abre a ficha de anamnese para quem pode ler; para quem não pode, a
 * linha diz quem lê. Não preenchida oferece reenviar e, a quem pode, cancelar.
 */
export function ActionsCell({
  anamnesis,
  busy,
  onResend,
  onCancel,
}: {
  anamnesis: AnamnesisSummary
  busy: boolean
  onResend: (target: ActionTarget) => void
  onCancel: (target: ActionTarget) => void
}) {
  const session = useSession()
  const name = titleCase(anamnesis.patient.name)
  const to = routes.patientAnamnesis(anamnesis.patient.id, anamnesis.id)

  if (anamnesis.status === 'completed' && !anamnesis.canRead) {
    return (
      <span className="font-body text-eyebrow text-label desk:max-w-[220px] block leading-[1.5] font-light tracking-normal">
        Leitura disponível para a profissional responsável e a gestora
      </span>
    )
  }

  return (
    <RowActions>
      <Link to={to} aria-label={`Abrir a anamnese de ${name}`}>
        {anamnesis.status === 'completed' ? 'abrir ficha' : 'ver envio'}
      </Link>
      {canResend(anamnesis) ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => onResend(anamnesis)}
          aria-label={`Reenviar a anamnese de ${name}`}
        >
          reenviar
        </button>
      ) : null}
      {canCancel(session, anamnesis) ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => onCancel(anamnesis)}
          aria-label={`Cancelar a anamnese de ${name}`}
        >
          cancelar
        </button>
      ) : null}
    </RowActions>
  )
}
